import ChartTooltip from '@lyra/core/components/ChartTooltip'
import Heading from '@lyra/core/components/Heading'
import LineChart from '@lyra/core/components/LineChart'
import Section, { SectionChartProps } from '@lyra/core/components/Section'
import { FundingChartAggregatePeriod } from '@lyra/core/constants/chart'
import formatDateTime from '@lyra/core/utils/formatDateTime'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import useFundingRateHistory, { FundingRateData } from '@lyra/web/hooks/useFundingRateHistory'
import React, { useCallback, useMemo, useState } from 'react'
import { useTheme, XStack } from 'tamagui'

import FundingChartIntervalSelector from './FundingChartIntervalSelector'

type Props = {
  instrumentName: string
} & SectionChartProps

const EMPTY_DATA: FundingRateData = {
  fundingRate: 0,
  timestamp: 0,
}

const FundingChart = ({ instrumentName, ...stackProps }: Props) => {
  const theme = useTheme()

  const [fundingPeriod, setFundingPeriod] = useState<FundingChartAggregatePeriod>(
    FundingChartAggregatePeriod.OneHour
  )

  const { data: fundingRateData = [], isLoading: loadingFundingRateHistory } =
    useFundingRateHistory(instrumentName)

  const formattedFundingRateData: FundingRateData[] = useMemo(() => {
    const formattedData = fundingRateData.map((item) => {
      switch (fundingPeriod) {
        case FundingChartAggregatePeriod.OneHour:
          return {
            fundingRate: item.fundingRate,
            timestamp: item.timestamp,
          }
        case FundingChartAggregatePeriod.EightHours:
          return {
            fundingRate: item.fundingRate * 8,
            timestamp: item.timestamp,
          }
        case FundingChartAggregatePeriod.OneYear:
          return {
            fundingRate: item.fundingRate * 24 * 365,
            timestamp: item.timestamp,
          }
      }
    })
    return formattedData
  }, [fundingPeriod, fundingRateData])

  const currentFundingRate = useMemo(
    () =>
      formattedFundingRateData.length
        ? formattedFundingRateData[formattedFundingRateData.length - 1]
        : EMPTY_DATA,
    [formattedFundingRateData]
  )

  const handleChangeInterval = useCallback((interval: FundingChartAggregatePeriod) => {
    setFundingPeriod(interval)
  }, [])

  const [minY, maxY] = useMemo(() => {
    if (formattedFundingRateData.length === 0) {
      return [0, 0]
    }

    const fundingRates = formattedFundingRateData
      .filter((item) => item && item.fundingRate !== undefined && item.fundingRate !== null)
      .map((item) => item.fundingRate)

    const minFundingRate = Math.min(...fundingRates)
    const maxFundingRate = Math.max(...fundingRates)

    const range = maxFundingRate - minFundingRate

    const padding = range === 0 ? Math.abs(maxFundingRate * 0.1) || 0.01 : range * 0.1

    const adjustedMinY = minFundingRate - padding
    const adjustedMaxY = maxFundingRate + padding

    return [adjustedMinY, adjustedMaxY]
  }, [formattedFundingRateData])

  return (
    <Section.Chart
      {...stackProps}
      header={
        <XStack
          flex={1}
          gap="$1"
          justifyContent="space-between"
          flexWrap="wrap"
          alignItems="flex-start"
          $mobile={{ flexDirection: 'column-reverse' }}
        >
          <Heading size="h4" color={currentFundingRate.fundingRate >= 0 ? 'green' : 'red'}>
            {formatPercentage(currentFundingRate.fundingRate, {
              showSign: true,
              dps: fundingPeriod === FundingChartAggregatePeriod.OneYear ? 3 : 4,
            })}
          </Heading>
          <FundingChartIntervalSelector
            selectedInterval={fundingPeriod}
            onChangeInterval={handleChangeInterval}
          />
        </XStack>
      }
      isLoading={loadingFundingRateHistory}
    >
      <LineChart
        height="100%"
        width="100%"
        data={formattedFundingRateData}
        dataKeys={[
          {
            key: 'fundingRate',
            color: '$primary',
          },
        ]}
        xAxisDataKey="timestamp"
        xAxisProps={{
          hide: false,
          tickCount: 8,
          tickFormatter: (v) => formatDateTime(v, { customFormat: 'd' }),
        }}
        yAxisProps={{
          hide: false,
          domain: [minY, maxY],
          tickCount: 8,
          tickFormatter: (v) =>
            formatNumber(v, {
              dps: fundingPeriod === FundingChartAggregatePeriod.OneYear ? 3 : 5,
            }),
        }}
        tooltipProps={{
          content: ({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload
              return (
                <ChartTooltip
                  xLabel={formatDateTime(data.timestamp, { hideYear: true })}
                  legend={[
                    {
                      dataKey: 'Funding Rate',
                      value: formatPercentage(data.fundingRate, {
                        showSign: true,
                        dps: fundingPeriod === FundingChartAggregatePeriod.OneYear ? 3 : 4,
                      }),
                      valueColor: data.fundingRate >= 0 ? 'green' : 'red',
                      color: data.fundingRate >= 0 ? 'green' : 'red',
                    },
                  ]}
                />
              )
            }
          },
        }}
        referenceLinesProps={[
          {
            id: 'zeroIndex',
            y: 0,
            label: '',
            color: theme.secondary?.get(),
            strokeDasharray: '6 6',
          },
        ]}
      />
    </Section.Chart>
  )
}

export default FundingChart
